import {
  ReferenceManyField,
  Datagrid,
  Pagination,
  useRefresh,
  useTranslate,
  useListContext,
} from "react-admin";
import { FunctionField } from "react-admin";
import { TextFieldMod } from "../../../../../Component/Layouts/Field";
import { NoResults } from "Component/NoResults/NoResults";
import React, { useEffect, useMemo } from "react";
import "./style.css";
import DateTimeFieldMod from "../../../../../Component/Redesign/DateTimeFieldMod";
import ChatButton from "../../../../../Component/Layouts/Button/ChatButton";

const MobileCardList = ({ doctorId, subscriptionStatus }) => {
    const { data, isLoading } = useListContext();
    const [expandedIds, setExpandedIds] = React.useState(new Set());

    const toggleExpand = (id) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    if (isLoading || !data) return null;
    if (!data.length) return <NoResults />;

    return (
        <>
            <div className="chat-card-list">
                {data.map((record) => {
                    const isExpanded = expandedIds.has(record.id);
                    return (
                        <div
                            key={record.id}
                            className="chat-card"
                            style={{
                                fontWeight: record.is_read ? 400 : 600,
                                backgroundColor: record.is_read ? "inherit" : "rgb(200 219 253)",
                            }}
                        >
                            {/* Collapsed header — always visible */}
                            <div className="chat-card-header">
                                <div className="chat-card-header-info">
                                    <div className="chat-card-header-info_content">
                                        <a
                                            target="_blank"
                                            rel="noreferrer"
                                            href={`#/users/${record.client_id}/show`}
                                            className="chat-card-header-patient"
                                        >
                                            {record.patient}
                                        </a>
                                        <span className="chat-card-header-date">
                                        {record.created_at
                                            ? new Intl.DateTimeFormat("en", {
                                                dateStyle: "short",
                                                timeStyle: "short"
                                            }).format(new Date(record.created_at))
                                            : "—"}
                                    </span>
                                    </div>
                                    <ChatButton
                                        userDoctorType={record.chat_type}
                                        id={record.client_id}
                                        doctorId={doctorId}
                                    />
                                    <button
                                        className={`chat-card-expand-btn ${isExpanded ? "expanded" : ""}`}
                                        onClick={() => toggleExpand(record.id)}
                                        aria-label={isExpanded ? "Collapse" : "Expand"}
                                    >
                                        <span>
                                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none"
                                                 xmlns="http://www.w3.org/2000/svg">
  <path d="M8 11 L14 17 L20 11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                                        </span>
                                    </button>
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="chat-card-body">
                                    <div className="chat-card-row">
                                    <span className="chat-card-label">Message:</span>
                                        <div className="chat-card-value_wrap">
                                            <span className="chat-card-value">
                                                {record.type === "text" ? record.text : "—"}
                                            </span>
                                </div>
                            </div>

                            <div className="chat-card-row">
                                <span className="chat-card-label">Type:</span>
                                <span className="chat-card-value">{record.type}</span>
                            </div>

                            <div className="chat-card-row last">
                                <span className="chat-card-label">Last message from:</span>
                                <span className="chat-card-value">{record.last_message_sender}</span>
                            </div>
                        </div>
                    )}
                </div>
                );
                })}
            </div>
        </>
    );
};

const ResponsiveChatsDatagrid = ({doctorId, subscriptionStatus}) => {
    const [isMobile, setIsMobile] = React.useState(
        () => window.innerWidth < 768
    );

    useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  if (isMobile) {
    return <MobileCardList doctorId={doctorId} subscriptionStatus={subscriptionStatus} />;
  }

  return (
      <Datagrid
          bulkActionButtons={false}
          empty={<NoResults />}
          rowStyle={(record) => ({
            fontWeight: record.is_read ? 400 : 600,
            backgroundColor: record.is_read ? "inherit" : "rgb(200 219 253)",
          })}
          rowClick={false}
          className="clients"
      >
        <FunctionField
            label="Message"
            render={(record) => (record.type === "text" ? <span title={record.text}>{record.text}</span> : "—")}
        />
        <TextFieldMod source="type" sortable={false} />
        <FunctionField
            source="patient"
            sortable={false}
            label="Patient"
            render={(record) => (
                <a target="_blank" rel="noreferrer" href={`#/users/${record.client_id}/show`}>
                  {record.patient}
                </a>
            )}
        />
        <TextFieldMod source="last_message_sender" label="Last message from" sortable={false} />
        <DateTimeFieldMod locales="en" showTime source="created_at" sortable={false} />
        <div className="end-chat-button">
          <FunctionField
              render={(record) => (
                  <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px"
                  }}>
                      <ChatButton
                          userDoctorType={record.chat_type}
                          id={record.client_id}
                          doctorId={doctorId}
                      />
                  </div>
              )}
          />
        </div>
      </Datagrid>
  );
};

const ChatsTab = (props) => {
  const { type, doctorId } = props;
  const translate = useTranslate();
  const refresh = useRefresh();

  const subscriptionStatus = useMemo(
      () => ({
        created: translate("subscription.statuses.created"),
        active: translate("subscription.statuses.active"),
        completed: translate("subscription.statuses.completed"),
        pending: translate("subscription.statuses.pending"),
        canceled: translate("subscription.statuses.canceled"),
        not_available: translate("subscription.statuses.notAvailable"),
      }),
      [translate]
  );

  const filters = {};
  if (type) filters["type"] = type;

  useEffect(() => {
    const interval = setInterval(() => {
      refresh();
    }, 3000);
    return () => clearInterval(interval);
  }, [refresh]);

  return (
      <div className="patients-tab-content">
        <ReferenceManyField
            reference="chat/tab"
            target="doctor1"
            label={false}
            pagination={<Pagination />}
            filter={filters}
        >
          <ResponsiveChatsDatagrid
              doctorId={doctorId}
              subscriptionStatus={subscriptionStatus}
          />
        </ReferenceManyField>
      </div>
  );
};

export default ChatsTab;